import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { useDebouncedCallback } from './useDebouncedCallback';

const DEBOUNCE_MS = 500;

export interface UseSearchInputOptions {
  onSearch: (term: string) => void;
  value?: string;
  defaultValue?: string;
  onChange?: (term: string) => void;
  threshold?: number;
  automatic?: boolean;
  enterToSubmit?: boolean;
}

export interface GetInputPropsOverrides {
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
}

export interface InputProps {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

export interface GetSubmitPropsOverrides {
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}

export interface SubmitProps {
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled: boolean;
}

export interface SearchInput {
  getInputProps: (overrides?: GetInputPropsOverrides) => InputProps;
  getSubmitProps: (overrides?: GetSubmitPropsOverrides) => SubmitProps;
  value: string;
  canSearch: boolean;
  submit: () => void;
  /**
   * Reset the term and fire a search for it (defaults to empty). In
   * uncontrolled mode this clears the internal value; in controlled mode
   * the caller is expected to also update its own state via onChange.
   */
  reset: (term?: string) => void;
}

export function useSearchInput({
  onSearch,
  value: controlledValue,
  defaultValue = '',
  onChange,
  threshold = 3,
  automatic = true,
  enterToSubmit = true,
}: UseSearchInputOptions): SearchInput {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const value = isControlled ? controlledValue : internalValue;

  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  const valueRef = useRef(value);
  valueRef.current = value;

  const canSubmit = useCallback(
    (term: string) => term.length === 0 || term.length >= threshold,
    [threshold]
  );

  const commit = useCallback(
    (term: string) => {
      if (canSubmit(term)) onSearchRef.current(term);
    },
    [canSubmit]
  );

  const debouncedCommit = useDebouncedCallback(commit, DEBOUNCE_MS);

  const commitNow = useCallback(
    (term: string) => {
      debouncedCommit.cancel();
      commit(term);
    },
    [debouncedCommit, commit]
  );

  const handleChange = useCallback(
    (next: string) => {
      valueRef.current = next;
      if (!isControlled) setInternalValue(next);
      onChange?.(next);
      if (automatic) debouncedCommit(next);
    },
    [isControlled, onChange, automatic, debouncedCommit]
  );

  const getInputProps = useCallback(
    (overrides?: GetInputPropsOverrides): InputProps => ({
      value,
      onChange: (event) => {
        overrides?.onChange?.(event);
        if (event.defaultPrevented) return;
        handleChange(event.target.value);
      },
      onKeyDown: (event) => {
        overrides?.onKeyDown?.(event);
        if (event.defaultPrevented) return;
        if (enterToSubmit && event.key === 'Enter') {
          commitNow(event.currentTarget.value);
        }
      },
    }),
    [value, handleChange, enterToSubmit, commitNow]
  );

  const getSubmitProps = useCallback(
    (overrides?: GetSubmitPropsOverrides): SubmitProps => ({
      disabled: overrides?.disabled ?? !canSubmit(value),
      onClick: (event) => {
        overrides?.onClick?.(event);
        if (event.defaultPrevented) return;
        commitNow(valueRef.current);
      },
    }),
    [canSubmit, value, commitNow]
  );

  const submit = useCallback(() => commitNow(valueRef.current), [commitNow]);

  const reset = useCallback(
    (term = '') => {
      valueRef.current = term;
      if (!isControlled) setInternalValue(term);
      onChange?.(term);
      commitNow(term);
    },
    [isControlled, onChange, commitNow]
  );

  return useMemo(
    () => ({
      getInputProps,
      getSubmitProps,
      value,
      canSearch: canSubmit(value),
      submit,
      reset,
    }),
    [getInputProps, getSubmitProps, value, canSubmit, submit, reset]
  );
}
